import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalStorage } from 'angular-web-storage';

import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { LocationService } from '../../../../../../../services/location/location.service';
import { ScheduledEventService } from '../../../../../../../services/scheduled-event/scheduled-event.service';
import {
  EventWaitlistFilters,
  EventWaitlistSummary,
  loadEventWaitlistFiltersFromGetParams
} from '../../../../../../../models/scheduled-event';
import { EventType } from '../../../../../../../models/event-type';
import { EventTypeStatus } from '../../../../../../../models/event-type-enums';
import {
  addFiltersToGetParams,
  CallbackResult,
  dateStringFromISO8601,
  getFilterTextForDateRange,
  propExists,
  propExistsAndIsNonEmpty,
  timeStringFromISO8601
} from '../../../../../../../utils';
import { getEventWaitlistSummaryRouterPath } from '../../../../../../../utils-models';
import { EventWaitlistsFiltersModalComponent } from './modals/event-waitlists-filters-modal/event-waitlists-filters-modal.component';
import { ApiCollectionExportModalComponent } from '../../../../modals/api-collection-export-modal/api-collection-export-modal.component';
import { COLUMN_HELP_TEXT, COLUMN_NAMES, makeGetContentItem } from '../../../../../../../models/exports/event-waitlist-summaries';

const DEFAULT_PAGE_SIZE = 25;
const EXPORT_PAGE_SIZE = 1000;


@Component({
  selector: 'app-event-waitlists',
  templateUrl: './event-waitlists.component.html',
  styleUrls: ['./event-waitlists.component.css']
})
export class EventWaitlistsComponent implements OnInit {

  @LocalStorage() public eventWaitlistsPageSize;

  public filters: EventWaitlistFilters = {};

  public summaries: EventWaitlistSummary[];
  public eventTypes: EventType[];

  public pageNumber = 1;
  public totalCount = 0;

  public loadingSummaries = true;
  public loadingEventTypes = true;

  public serverError: number = null;
  public successMessage = '';

  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;
  public getEventWaitlistSummaryRouterPath = getEventWaitlistSummaryRouterPath;

  // Guards against an older, slower response overwriting a newer one.
  private loadSummariesRequestCount = 0;
  // Fixed when the filters are applied, so paging does not move the lower
  // bound between pages.
  private defaultWindowStart: string;

  constructor(
    public accountService: AccountService,
    public locationService: LocationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private scheduledEventService: ScheduledEventService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.eventWaitlistsPageSize) {
      this.eventWaitlistsPageSize = DEFAULT_PAGE_SIZE;
    }
    // @LocalStorage() drops the first write per page load; burn it here so the
    // user's first page-size change persists.
    // noinspection SillyAssignmentJS
    this.eventWaitlistsPageSize = this.eventWaitlistsPageSize;

    this.defaultWindowStart = new Date().toISOString();
    this.filters = loadEventWaitlistFiltersFromGetParams(this.activatedRoute);

    if (!this.activatedRoute.snapshot.queryParamMap.has('location_id')) {
      this.filters.location_id = this.accountService.getMyAccount().default_gym_id;
    }

    this.loadSummaries();
    this.loadEventTypes();
  }

  public loadSummaries() {
    this.loadingSummaries = true;

    this.loadSummariesRequestCount++;
    const thisRequestCount = this.loadSummariesRequestCount;

    this.scheduledEventService
      .getEventWaitlistSummaries(this.pageNumber, this.getPageSize(), this.getRequestParams())
      .then(response => {
        if (thisRequestCount !== this.loadSummariesRequestCount) { return; }

        this.summaries = response.results;
        this.totalCount = response.count;
      })
      .catch(reason => {
        if (thisRequestCount !== this.loadSummariesRequestCount) { return; }

        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount !== this.loadSummariesRequestCount) { return; }

        this.loadingSummaries = false;
      });
  }

  public setPage(pageNumber: number) {
    this.pageNumber = pageNumber;
    this.loadSummaries();
  }

  public handlePageSizeChange() {
    this.pageNumber = 1;
    this.loadSummaries();
  }

  public lastPageNumber(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.getPageSize()));
  }

  public showLocationColumn(): boolean {
    const locations = this.locationService.getLocations();
    return (locations !== null) && (locations.length > 1);
  }

  public getEventDisplay(summary: EventWaitlistSummary): string {
    let display = summary.event_type_name;
    if (summary.occurrence_name) {
      display += ' - ' + summary.occurrence_name;
    }
    if (summary.event_part_count > 1) {
      display += ' (' + summary.event_part_count + ' parts)';
    }
    return display;
  }

  public getActiveFilters(): string[] {
    const activeFilters: string[] = [];

    const fs = this.filters;

    if (this.locationService.getLocations() === null) {
      activeFilters.push('Loading...');
    } else if (this.locationService.getLocations().length > 1) {
      if (propExists(fs.location_id)) {
        const location = this.getLocationForId(fs.location_id);
        if (location !== null) {
          activeFilters.push('Event is located at ' + location.name);
        } else {
          activeFilters.push('Event is located at "[unknown location]"');
        }
      } else {
        activeFilters.push('Event is at any location');
      }
    }

    if (propExists(fs.event_type_id)) {
      if (this.loadingEventTypes) {
        activeFilters.push('Loading...');
      } else {
        const eventType = this.getEventTypeForId(fs.event_type_id);
        if (eventType !== null) {
          activeFilters.push('Event type is "' + eventType.name + '"');
        } else {
          activeFilters.push('Event type is "[unknown event type]"');
        }
      }
    }

    if (!this.hasDateFilter()) {
      activeFilters.push('Event has not yet started');
    } else {
      activeFilters.push(getFilterTextForDateRange('Event starts', fs.first_start_min, fs.first_start_max));
    }

    if (fs.has_active_waitlist === false) {
      activeFilters.push('Including events with no waitlist entries');
    } else {
      activeFilters.push('Has waiting or pending waitlist entries');
    }

    return activeFilters;
  }

  public openEditFiltersModal() {
    if (this.loadingFiltersModalRequirements()) { return; }

    const modalRef = this.modalService.open(EventWaitlistsFiltersModalComponent);
    modalRef.componentInstance.inputFilters = this.filters;
    modalRef.componentInstance.eventTypes = this.eventTypes;
    modalRef.componentInstance.savedCallback = (newFilters: EventWaitlistFilters) => {
      this.filters = newFilters;
      this.defaultWindowStart = new Date().toISOString();
      addFiltersToGetParams(this.filters, this.router, this.activatedRoute, ['location_id']);
      this.pageNumber = 1;
      this.loadSummaries();
    };
  }

  public loadingFiltersModalRequirements(): boolean {
    return this.loadingEventTypes || (this.locationService.getLocations() === null);
  }

  public openExportListModal() {
    if (this.loadingSummaries) { return; }

    this.successMessage = '';

    const modalRef = this.modalService.open(ApiCollectionExportModalComponent);
    modalRef.componentInstance.getPage = (pageNum: number) => {
      return this.scheduledEventService.getEventWaitlistSummaries(pageNum, EXPORT_PAGE_SIZE, this.getRequestParams());
    };
    modalRef.componentInstance.getContentItem = makeGetContentItem(
      summary => window.location.origin + getEventWaitlistSummaryRouterPath(summary).join('/') + '#waitlist'
    );
    modalRef.componentInstance.totalNumPages = Math.max(1, Math.ceil(this.totalCount / EXPORT_PAGE_SIZE));
    modalRef.componentInstance.columnNames = COLUMN_NAMES;
    modalRef.componentInstance.columnHelpText = COLUMN_HELP_TEXT;
    modalRef.componentInstance.exportNameHumanReadable = 'Individual Event Waitlists';
    modalRef.componentInstance.exportFilenamePrefix = 'Individual_Event_Waitlists';
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.successMessage = result.text;
    };
  }

  private hasDateFilter(): boolean {
    return propExistsAndIsNonEmpty(this.filters.first_start_min) ||
      propExistsAndIsNonEmpty(this.filters.first_start_max);
  }

  /**
   * The filters plus the defaults the server does not apply. Kept out of
   * `filters` so the URL only carries what the user chose.
   */
  private getRequestParams(): EventWaitlistFilters {
    const params: EventWaitlistFilters = {...this.filters};

    if (!propExistsAndIsNonEmpty(params.first_start_min)) { delete params.first_start_min; }
    if (!propExistsAndIsNonEmpty(params.first_start_max)) { delete params.first_start_max; }
    if (!this.hasDateFilter()) {
      params.first_start_min = this.defaultWindowStart;
    }

    if (!propExists(params.has_active_waitlist)) {
      params.has_active_waitlist = true;
    }

    return params;
  }

  private getPageSize(): number {
    return this.eventWaitlistsPageSize || DEFAULT_PAGE_SIZE;
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(1, 3000)
      .then(response => {
        this.eventTypes = response.results
          .filter((value) => {
            return (value.status !== EventTypeStatus.draft);
          })
          .sort((a, b) => {
            if (a.status !== b.status) {
              if (a.status === EventTypeStatus.active) { return -1; }
              else { return 1; }
            }
            return (a.name > b.name) ? 1 : -1;
          });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private getEventTypeForId(eventTypeId: number): EventType | null {
    for (const eventType of this.eventTypes) {
      if (eventType.id === eventTypeId) {
        return eventType;
      }
    }

    return null;
  }

  private getLocationForId(locationId: number) {
    for (const location of this.locationService.getLocations()) {
      if (location.id === locationId) {
        return location;
      }
    }

    return null;
  }

}
